<script setup>
import { ref } from 'vue';
import { Link, router, useForm } from '@inertiajs/vue3';
import ActionMessage from '@/Components/ActionMessage.vue';
import FormSection from '@/Components/FormSection.vue';
import InputError from '@/Components/InputError.vue';
import InputLabel from '@/Components/InputLabel.vue';
import PrimaryButton from '@/Components/PrimaryButton.vue';
import SecondaryButton from '@/Components/SecondaryButton.vue';
import TextInput from '@/Components/TextInput.vue';
import defaultLogo from './../../../../assets/images/atmetica-logo.svg';
import defaultLogoLight from './../../../../assets/images/atmetica-logo-light.svg';
import { toast } from 'vue3-toastify';

const props = defineProps({
    brand: Object,
});

const form = useForm({
    custom_error: '',
    brand_name: props.brand?.name ?? '',
    from_email: props.brand?.from_email ?? '',
    logo: null,
    logo_dark: null,
});

const logoPreview = ref(null);
const logoInput = ref(null);

const logoDarkPreview = ref(null);
const logoDarkInput = ref(null);

const updateBrandInformation = () => {
    if (logoInput.value) {
        form.logo = logoInput.value.files[0];
    }
    if (logoDarkInput.value) {
        form.logo_dark = logoDarkInput.value.files[0];
    }

    form.post(route('brand.update'), {
        errorBag: 'updateBrandInformation',
        preserveScroll: true,
        onSuccess: () => {
            toast.success('Brand information is updated');
            clearLogoFileInput();
            clearLogoDarkFileInput();
        },
    });
};

const selectNewLogo = () => {
    logoInput.value.click();
};

const updateLogoPreview = () => {
    const logo = logoInput.value.files[0];

    if (! logo) return;

    const reader = new FileReader();

    reader.onload = (e) => {
        logoPreview.value = e.target.result;
    };

    reader.readAsDataURL(logo);
};

const selectNewLogoDark = () => {
    logoDarkInput.value.click();
};

const updateLogoDarkPreview = () => {
    const logoDark = logoDarkInput.value.files[0];

    if (! logoDark) return;

    const reader = new FileReader();

    reader.onload = (e) => {
        logoDarkPreview.value = e.target.result;
    };

    reader.readAsDataURL(logoDark);
};

const deleteLogo = () => {
    router.delete(route('brand.deleteLogo'), {
        preserveScroll: true,
        onSuccess: () => {
            logoPreview.value = null;
            clearLogoFileInput();
        },
    });
};

const deleteLogoDark = () => {
    router.delete(route('brand.deleteLogoDark'), {
        preserveScroll: true,
        onSuccess: () => {
            logoDarkPreview.value = null;
            clearLogoDarkFileInput();
        },
    });
};


const clearLogoFileInput = () => {
    if (logoInput.value?.value) {
        logoInput.value.value = null;
    }
};

const clearLogoDarkFileInput = () => {
    if (logoDarkInput.value?.value) {
        logoDarkInput.value.value = null;
    }
};
</script>

<template>
    <form @submit.prevent="updateBrandInformation">
        <v-row>
            <v-col cols="12" sm="4">
                <div class="text-h6 pb-1">Brand Information</div>
                <div class="text-body-2 text-secondary">Update your Company details here for branding purpose.</div>
            </v-col>
            <v-col cols="12" sm="8">
                <v-card>
                    <v-card-text>
                        <div class="d-flex flex-column ga-2">
                            <div class="d-flex ga-5 flex-wrap">
                                <div class="w-25rem max-w-100 border rounded-lg px-3 py-3 pb-4 bg-surface v-theme--light">
                                    <form-label title="Logo for light theme:" class="mb-4"></form-label>
                                    <input
                                            ref="logoInput"
                                            type="file"
                                            class="d-none"
                                            @change="updateLogoPreview"
                                        >

                                        <!-- <InputLabel for="photo" value="Photo" /> -->

                                        <!-- Current Profile Photo -->
                                        <div v-show="! logoPreview" class="mt-2">
                                            <v-avatar rounded="0" size="100px" :alt="brand?.name" class="v-theme--light">
                                                <v-img :src="brand?.company_logo_url || defaultLogo" :cover="false"></v-img>
                                            </v-avatar>
                                        </div>

                                        <!-- New Profile Photo Preview -->
                                        <div v-show="logoPreview" class="mt-2">
                                            <v-avatar rounded="0" size="100px" class="v-theme--light">
                                                <v-img :src="logoPreview" :cover="false"></v-img>
                                            </v-avatar>
                                        </div>

                                        <div class="d-flex ga-2 mt-4 flex-wrap">
                                            <v-btn color="secondary" variant="outlined" text="Select A New Logo" class="v-theme--light" @click.prevent="selectNewLogo"></v-btn>
                                            <v-btn v-if="brand?.logo_path" color="danger" variant="outlined" text="Remove Logo" class="v-theme--light" @click.prevent="deleteLogo"></v-btn>

                                        </div>

                                        <InputError :message="form.errors.logo" class="mt-2 v-theme--light" />
                                </div>
                                <div class="w-25rem max-w-100 border rounded-lg px-3 py-3 pb-4 bg-surface v-theme--dark">
                                    <form-label title="Logo for dark theme:" class="mb-4"></form-label>
                                    <input
                                            ref="logoDarkInput"
                                            type="file"
                                            class="d-none"
                                            @change="updateLogoDarkPreview"
                                        >

                                        <!-- <InputLabel for="photo" value="Photo" /> -->

                                        <!-- Current Profile Photo -->
                                        <div v-show="! logoDarkPreview" class="mt-2">
                                            <v-avatar rounded="0" size="100px" :alt="brand?.name" class="v-theme--dark">
                                                <v-img :src="brand?.company_logo_dark_url || defaultLogoLight" :cover="false"></v-img>
                                            </v-avatar>
                                        </div>

                                        <!-- New Profile Photo Preview -->
                                        <div v-show="logoDarkPreview" class="mt-2">
                                            <v-avatar rounded="0" size="100px"  class="v-theme--dark">
                                                <v-img :src="logoDarkPreview" :cover="false"></v-img>
                                            </v-avatar>
                                        </div>

                                        <div class="d-flex ga-2 mt-4 flex-wrap">
                                            <v-btn color="secondary" variant="outlined" text="Select A New Logo" class="v-theme--dark" @click.prevent="selectNewLogoDark"></v-btn>
                                            <v-btn v-if="brand?.logo_dark_path" color="danger" variant="outlined" text="Remove Logo" class="v-theme--dark" @click.prevent="deleteLogoDark"></v-btn>

                                        </div>

                                        <InputError :message="form.errors.logo_dark" class="mt-2 v-theme--dark" />
                                </div>
                            </div>
                            <div class="text-caption">You shoud upload png or svg image with transparent backgrounf.</div>
                            <v-row>
                                <v-col cols="12" md="8" xl="7" class="d-flex flex-column ga-4">
                                    <div class="pb-4">
                                        
                                    </div>
                                    <div>
                                        <v-text-field
                                            v-model="form.brand_name"
                                            label="Brand Name"
                                            variant="outlined"
                                            :error-messages="form.errors.brand_name"
                                            required
                                        ></v-text-field>
                                    </div>
                                    <div>
                                        <v-text-field
                                            v-model="form.from_email"
                                            label="From Email"
                                            variant="outlined"
                                            :error-messages="form.errors.from_email"
                                            required
                                            hide-details="auto"
                                        ></v-text-field>
                                        <div class="text-caption mt-2 text-muted">This email will be used as <strong class="text-secondary">"From Address"</strong> when we need to send email notifications to your Card holders.</div>
                                    </div>
                                </v-col>
                            </v-row>
                            <div class="fs-1 text-error pt-3" v-if="form.errors.custom_error">{{ form.errors.custom_error }}</div>
                        </div>
                        
                    </v-card-text>
                    <v-card-actions-default>
                        <div class="d-flex justify-space-between align-center flex-grow-1 ga-4">
                            <div>
                                <span class="text-success" v-if="form.recentlySuccessful">
                                    <span class="fa fa-check"></span> Saved
                                </span>
                            </div>
                            <v-btn-input-compact type="submit" color="primary" text="Save" :disabled="form.processing" :loading="form.processing">
                                <template #prepend>
                                    <mdi-icon>check</mdi-icon>
                                </template>
                            </v-btn-input-compact>
                        </div>
                    </v-card-actions-default>
                </v-card>
            </v-col>
        </v-row>
    </form>
</template>
